import React, { useState, useEffect } from 'react';
import {
  CogIcon,
  LinkIcon,
  QrCodeIcon,
  DevicePhoneMobileIcon,
  CreditCardIcon,
  EnvelopeIcon,
  CloudIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  NoSymbolIcon,
  BellIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../../config/api';
import AlimtalkSettings from './AlimtalkSettings';

interface IntegrationSettingsData {
  kakao_alimtalk_enabled: boolean;
  kakao_alimtalk_entry_exit: boolean;
  kakao_alimtalk_ticket_expiry: boolean;
  kakao_alimtalk_study_report: boolean;
  kakao_alimtalk_absent_alert: boolean;  // 미출석 알림
  sms_enabled: boolean;
  sms_fallback_enabled: boolean;
  sms_sender_number: string | null;
  kiosk_enabled: boolean;
  qrcode_enabled: boolean;
  payment_enabled: boolean;
  backup_enabled: boolean;
  block_expired_entry: boolean;  // 이용권 만료 회원 입장 차단
}

interface IntegrationItem {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  status: 'connected' | 'disconnected' | 'error';
  category: 'payment' | 'communication' | 'device' | 'security' | 'other';
  lastSync?: string;
  apiKey?: keyof IntegrationSettingsData;
  comingSoon?: boolean;
}

const IntegrationSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'payment' | 'communication' | 'device' | 'security'>('all');
  const [settings, setSettings] = useState<IntegrationSettingsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 연동 가능한 서비스 목록
  const getIntegrations = (): IntegrationItem[] => {
    return [
      {
        id: 'kiosk',
        name: '키오스크 연동',
        description: '독서실 입구 키오스크와 실시간 연동',
        icon: DevicePhoneMobileIcon,
        status: settings?.kiosk_enabled ? 'connected' : 'disconnected',
        category: 'device',
        lastSync: settings?.kiosk_enabled ? '실시간' : undefined,
        apiKey: 'kiosk_enabled',
        comingSoon: true,
      },
      {
        id: 'payment',
        name: '결제 연동',
        description: '통합 결제 서비스 연동',
        icon: CreditCardIcon,
        status: settings?.payment_enabled ? 'connected' : 'disconnected',
        category: 'payment',
        apiKey: 'payment_enabled',
        comingSoon: true,
      },
      {
        id: 'sms',
        name: 'SMS 알림 서비스',
        description: 'SMS 알림 발송 (알림톡 실패 시 자동 대체 발송)',
        icon: EnvelopeIcon,
        status: settings?.sms_enabled ? 'connected' : 'disconnected',
        category: 'communication',
        apiKey: 'sms_enabled',
        comingSoon: true,
      },
      // 제거(2026-07-05): '카카오톡 알림톡'(아래 상세설정 마스터와 중복),
      //   'QR 코드 체크인'·'자동 백업'(준비중 카드 정리).
    ];
  };

  const integrations = getIntegrations();

  // 초기 데이터 로드
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setInitialLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      if (!token) {
        setError('로그인이 필요합니다.');
        setInitialLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/owner/integration-settings`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('오너 권한이 필요합니다.');
        }
        throw new Error('설정을 불러오는데 실패했습니다.');
      }

      const data = await response.json();
      setSettings(data);
    } catch (err) {
      console.error('Failed to load integration settings:', err);
      setError(err instanceof Error ? err.message : '설정을 불러오는데 실패했습니다.');
    } finally {
      setInitialLoading(false);
    }
  };

  // 카테고리별 필터링
  const filteredIntegrations = activeTab === 'all'
    ? integrations
    : integrations.filter(item => item.category === activeTab);

  const getStatusBadge = (status: IntegrationItem['status']) => {
    switch (status) {
      case 'connected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="h-3 w-3 mr-1" />
            활성화
          </span>
        );
      case 'disconnected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <XCircleIcon className="h-3 w-3 mr-1" />
            비활성화
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
            오류
          </span>
        );
    }
  };

  const handleToggleIntegration = async (integration: IntegrationItem) => {
    const isCommService = integration.category === 'communication';
    const currentStatus = integration.status;

    if (currentStatus === 'connected') {
      const message = isCommService
        ? `${integration.name}을(를) 비활성화하시겠습니까?\n비활성화 시 회원들에게 알림이 전송되지 않습니다.`
        : `${integration.name} 연동을 해제하시겠습니까?`;

      if (!window.confirm(message)) {
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('로그인이 필요합니다.');
      }

      // API 서비스 매핑 (id → API service name)
      const serviceMap: Record<string, string> = {
        'kiosk': 'kiosk',
        'qrcode': 'qrcode',
        'payment': 'payment',
        'sms': 'sms',
        'kakao': 'kakao_alimtalk',
        'backup': 'backup',
      };

      const serviceName = serviceMap[integration.id];
      if (!serviceName) {
        throw new Error('지원하지 않는 서비스입니다.');
      }

      const response = await fetch(
        `${API_BASE_URL}/api/v1/owner/integration-settings/toggle/${serviceName}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('오너 권한이 필요합니다.');
        }
        throw new Error('설정 변경에 실패했습니다.');
      }

      const result = await response.json();

      // 설정 다시 로드
      await loadSettings();

      alert(result.message || '연동 설정이 변경되었습니다.');
    } catch (err) {
      console.error('Failed to toggle integration:', err);
      const errorMessage = err instanceof Error ? err.message : '설정 변경에 실패했습니다.';
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ArrowPathIcon className="h-12 w-12 text-gray-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">설정을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">오류</h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
              <button
                onClick={loadSettings}
                className="mt-3 text-sm font-medium text-red-800 hover:text-red-900"
              >
                다시 시도
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <LinkIcon className="h-8 w-8 mr-3 text-primary-600" />
          연동 설정
        </h1>
        <p className="text-gray-600 mt-1">
          외부 서비스와 연동하여 더 많은 기능을 사용하세요
        </p>
      </div>

      {/* 오류 메시지 */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* 탭 메뉴 */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {[
              { id: 'all', name: '전체', count: integrations.length },
              { id: 'payment', name: '결제', count: integrations.filter(i => i.category === 'payment').length },
              { id: 'communication', name: '알림', count: integrations.filter(i => i.category === 'communication').length },
              { id: 'device', name: '기기', count: integrations.filter(i => i.category === 'device').length },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-6 text-sm font-medium border-b-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.name}
                <span className="ml-2 text-xs text-gray-400">({tab.count})</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* 연동 목록 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredIntegrations.map((integration) => (
          <div
            key={integration.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className="p-2 bg-gray-100 rounded-lg mr-3">
                  <integration.icon className="h-6 w-6 text-gray-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    {integration.name}
                    {integration.comingSoon && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-medium">
                        준비중
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">{integration.description}</p>
                </div>
              </div>
              {!integration.comingSoon && getStatusBadge(integration.status)}
            </div>

            {integration.lastSync && integration.status === 'connected' && (
              <div className="text-sm text-gray-500 mb-4 flex items-center">
                <ArrowPathIcon className="h-4 w-4 mr-1" />
                마지막 동기화: {integration.lastSync}
              </div>
            )}

            <div className="flex justify-end">
              {integration.comingSoon ? (
                <button
                  disabled
                  className="px-4 py-2 bg-gray-100 text-gray-400 rounded-lg cursor-not-allowed text-sm font-medium"
                >
                  준비중
                </button>
              ) : integration.status === 'connected' ? (
                <button
                  onClick={() => handleToggleIntegration(integration)}
                  disabled={loading}
                  className="px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? '처리 중...' : '비활성화'}
                </button>
              ) : (
                <button
                  onClick={() => handleToggleIntegration(integration)}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? '처리 중...' : '활성화'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* 카카오 알림톡 상세 설정 — '전체'·'알림' 탭에서만 노출 (탭 무관 항상표시 버그 수정 2026-07-05) */}
      {(activeTab === 'all' || activeTab === 'communication') && (
        <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-yellow-100 rounded-lg mr-3">
              <BellIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <h2 className="text-lg font-medium text-gray-900">카카오 알림톡 상세 설정</h2>
              <p className="text-sm text-gray-500">전체 · 카테고리 · 알림 유형별 발송 여부를 설정합니다</p>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-4">
            <AlimtalkSettings />
          </div>
        </div>
      )}

      {/* 출입 관리 설정 */}
      <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <div className="p-2 bg-orange-100 rounded-lg mr-3">
            <NoSymbolIcon className="h-6 w-6 text-orange-600" />
          </div>
          <div>
            <h2 className="text-lg font-medium text-gray-900">출입 관리 설정</h2>
            <p className="text-sm text-gray-500">이용권 만료 회원의 입장 제어 설정</p>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
          {/* 현재 상태 카드 */}
          <div className={`p-4 rounded-lg mb-4 ${settings?.block_expired_entry === true ? 'bg-orange-100 border-2 border-orange-400' : 'bg-green-100 border-2 border-green-400'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {settings?.block_expired_entry === true ? (
                  <>
                    <NoSymbolIcon className="h-8 w-8 text-orange-600 mr-3" />
                    <div>
                      <span className="text-lg font-bold text-orange-800">🚫 입장 차단 ON</span>
                      <p className="text-sm text-orange-700">만료 회원은 입장할 수 없습니다</p>
                    </div>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-8 w-8 text-green-600 mr-3" />
                    <div>
                      <span className="text-lg font-bold text-green-800">✅ 입장 차단 OFF</span>
                      <p className="text-sm text-green-700">만료 회원도 입장할 수 있습니다</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* 설정 변경 버튼 */}
          <div className="flex gap-3">
            <button
              onClick={async () => {

                if (settings?.block_expired_entry === true) {
                  alert('이미 입장 차단이 활성화되어 있습니다.\n\n차단을 해제하려면 "차단 해제" 버튼을 눌러주세요.');
                  return;
                }

                const confirmed = window.confirm('이용권 만료 회원의 입장을 차단하시겠습니까?\n\n⚠️ 주의: 모든 회원의 이용권 정보가 입력되어 있어야 합니다.');
                if (!confirmed) return;

                setLoading(true);
                try {
                  const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');

                  const response = await fetch(
                    `${API_BASE_URL}/api/v1/owner/integration-settings`,
                    {
                      method: 'PATCH',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                      },
                      body: JSON.stringify({ block_expired_entry: true }),
                    }
                  );

                  const responseData = await response.json();

                  if (!response.ok) throw new Error('설정 변경 실패');
                  await loadSettings();
                  alert('✅ 입장 차단이 활성화되었습니다.');
                } catch (err) {
                  console.error('[입장차단] 설정 변경 실패:', err);
                  alert('설정 변경에 실패했습니다.\n\n' + (err instanceof Error ? err.message : ''));
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading || settings?.block_expired_entry === true}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                settings?.block_expired_entry === true
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-orange-500 hover:bg-orange-600 text-white'
              } ${loading ? 'opacity-50' : ''}`}
            >
              🚫 입장 차단 활성화
            </button>
            <button
              onClick={async () => {

                if (settings?.block_expired_entry !== true) {
                  alert('이미 입장 차단이 비활성화되어 있습니다.');
                  return;
                }

                const confirmed = window.confirm('이용권 만료 회원의 입장 차단을 해제하시겠습니까?\n\n만료 회원도 입장할 수 있게 됩니다.');
                if (!confirmed) return;

                setLoading(true);
                try {
                  const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');

                  const response = await fetch(
                    `${API_BASE_URL}/api/v1/owner/integration-settings`,
                    {
                      method: 'PATCH',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                      },
                      body: JSON.stringify({ block_expired_entry: false }),
                    }
                  );

                  const responseData = await response.json();

                  if (!response.ok) throw new Error('설정 변경 실패');
                  await loadSettings();
                  alert('✅ 입장 차단이 해제되었습니다.');
                } catch (err) {
                  console.error('[입장차단] 설정 변경 실패:', err);
                  alert('설정 변경에 실패했습니다.\n\n' + (err instanceof Error ? err.message : ''));
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading || settings?.block_expired_entry !== true}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                settings?.block_expired_entry !== true
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-green-500 hover:bg-green-600 text-white'
              } ${loading ? 'opacity-50' : ''}`}
            >
              ✅ 차단 해제
            </button>
          </div>
        </div>
      </div>

      {/* 안내 메시지 */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">연동 안내</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>결제 연동은 별도의 가맹점 신청이 필요합니다</li>
                <li>SMS 및 카카오톡 알림은 어텐드가 제공하는 서비스입니다</li>
                <li>카카오톡 알림톡 실패 시 자동으로 SMS로 대체 발송됩니다</li>
                <li>활성화/비활성화 설정은 즉시 적용됩니다</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationSettings;
